import { EntityLinks, Goal, ID } from '@griply/types';

import { getDirectGoalLifeAreaIds } from '../get-goal-life-area';
import { getGoalParentIds } from './get-goal-parents';

export enum GoalParentKind {
  Goal = 'goal',
  LifeArea = 'lifeArea',
}

export type ResolvedGoalParent<LifeAreaLike extends { id: ID }> =
  | { kind: GoalParentKind.Goal; goal: Goal }
  | { kind: GoalParentKind.LifeArea; lifeArea: LifeAreaLike };

export type ResolveGoalParentOptions<LifeAreaLike extends { id: ID }> = {
  goals: Goal[];
  lifeAreas: LifeAreaLike[];
  // Without links a migrated goal resolves to no parents at all.
  links?: EntityLinks;
};

// Every parent goal (in link order), then every directly linked life area.
export const resolveGoalParents = <LifeAreaLike extends { id: ID }>(
  goal: Goal,
  { goals, lifeAreas, links }: ResolveGoalParentOptions<LifeAreaLike>,
): ResolvedGoalParent<LifeAreaLike>[] => {
  const parentGoals = getGoalParentIds(goal, { links }).flatMap(
    (parentId): ResolvedGoalParent<LifeAreaLike>[] => {
      const parentGoal = goals.find(({ id }) => id === parentId);
      return parentGoal
        ? [{ kind: GoalParentKind.Goal, goal: parentGoal }]
        : [];
    },
  );
  const parentLifeAreas = getDirectGoalLifeAreaIds(goal, links).flatMap(
    (lifeAreaId): ResolvedGoalParent<LifeAreaLike>[] => {
      const lifeArea = lifeAreas.find(({ id }) => id === lifeAreaId);
      return lifeArea ? [{ kind: GoalParentKind.LifeArea, lifeArea }] : [];
    },
  );

  return [...parentGoals, ...parentLifeAreas];
};

export const resolveGoalParent = <LifeAreaLike extends { id: ID }>(
  goal: Goal,
  options: ResolveGoalParentOptions<LifeAreaLike>,
): ResolvedGoalParent<LifeAreaLike> | undefined =>
  resolveGoalParents(goal, options)[0];
